import { getRandomInt } from "../Shared/Helpers.tsx";
import { generateProblemPages } from "../Shared/Generation/problemPages.ts";
import { formatScaled, rescale } from "../Shared/scaledDecimal.ts";
import {
    ProblemData,
    RoundingComparingOptions,
    RoundingDisplayOptions,
    RoundingNumberLineSpec,
    RoundingPlaceOptions,
} from "./Constants.ts";
import { ISettingsProps } from "./Models.ts";
import { withThousands } from "./PlaceValueGenerator.ts";
import { PrintedLanguage, printedLanguageOf } from "./PrintedLanguage.ts";
import { roundClauses as enClauses, roundingPrompts as en } from "../Files/Prompts/en/roundingComparing.ts";
import { roundClauses as esClauses, roundingPrompts as es } from "../Files/Prompts/es/roundingComparing.ts";
import { roundClauses as frClauses, roundingPrompts as fr } from "../Files/Prompts/fr/roundingComparing.ts";

const PROMPTS: Record<PrintedLanguage, typeof en> = { en, es, fr };
const ROUND_CLAUSES: Record<PrintedLanguage, typeof enClauses> = { en: enClauses, es: esClauses, fr: frClauses };

/**
 * Rounding and comparing problem generation.
 *
 * Not arithmetic: a rounding question has one number in and one out, a
 * comparison has two numbers and a symbol between them. Both carry their prompt
 * and answer as text in ProblemData.display, the shape Place Value established.
 * See ADR 0006.
 *
 * Every value is held as a scaled integer, whole numbers included (at scale 0),
 * so 0.7 vs 0.68 is decided by comparing integers - not strings ("0.68" sorts
 * after "0.7"), and not floats.
 */

/** The place a rounding target sits at, as a power of ten. Tenth is -1. */
const PLACE_EXPONENT: Record<string, number> = {
    [RoundingPlaceOptions.Ten]: 1,
    [RoundingPlaceOptions.Hundred]: 2,
    [RoundingPlaceOptions.Thousand]: 3,
    [RoundingPlaceOptions.Whole]: 0,
    [RoundingPlaceOptions.Tenth]: -1,
};

/**
 * Whether a rounding place is precise enough for a number line (ADR 0046
 * §2): the number has to be exact to one place finer than the rounding
 * place, or nothing in it can land on a tick. Nearest ten always qualifies -
 * a whole number is already exact to the ones. Nearest whole needs the sheet
 * held to one decimal place; nearest tenth needs two. Nearest hundred and
 * thousand never qualify at any precision this format offers - the line
 * would need either an unreadable tick count or a number stripped of the
 * digit the question is about, and ADR 0046 §2 refuses both.
 */
function roundingLineEligible(exponent: number, decimals: number): boolean {
    if (decimals === 0) return exponent === PLACE_EXPONENT[RoundingPlaceOptions.Ten];
    if (decimals === 1) return exponent === PLACE_EXPONENT[RoundingPlaceOptions.Whole];
    if (decimals === 2) return exponent === PLACE_EXPONENT[RoundingPlaceOptions.Tenth];
    return false;
}

/**
 * How often to label a tick on a rounding or comparing number line.
 *
 * `NumberLine.tsx` labels by tick index (`i % labelEvery === 0`), so
 * `labelEvery` has to divide `steps` or the last tick would silently go
 * unlabelled. Same problem elapsed time on a number line solved
 * (`labelEveryForElapsed` in TimeGenerator.ts, ADR 0044 §2), solved the same
 * way here: search upward from `steps / 8` for the first divisor, which
 * always finds one (`steps` itself, at worst) and always includes both ends.
 */
function lineLabelEvery(steps: number): number {
    if (steps <= 8) {
        return 1;
    }
    for (let candidate = Math.ceil(steps / 8); candidate <= steps; candidate++) {
        if (steps % candidate === 0) {
            return candidate;
        }
    }
    return steps;
}

/**
 * The number line for a rounding problem: `from`/`to` are the two rounding
 * candidates, ten ticks apart (ADR 0046 §2's table - one place finer than the
 * rounding place always divides the step into exactly ten). The one
 * worksheet mark is the number being rounded, never the rounded answer.
 *
 * Returns `undefined` when the number is already an exact multiple of the
 * rounding place - "round 40 to the nearest ten" - because the marker would
 * then sit exactly on one of the two labelled candidates and identify it as
 * the answer outright, which is exactly what ADR 0046 §4 forbids. The builders
 * move such a number off the tick before they ask (`offTheTick`), so on a sheet
 * that draws lines this is a guard and no longer a fallback; a rounding place
 * the display never reaches still prints plain.
 */
function roundingLineSpec(
    valueScaled: number,
    roundedScaled: number,
    scaleK: number,
    step: number
): RoundingNumberLineSpec | undefined {
    const offset = valueScaled % step;
    if (offset === 0) {
        return undefined;
    }
    const fromScaled = valueScaled - offset;
    const toScaled = fromScaled + step;
    const divisor = 10 ** scaleK;
    return {
        from: fromScaled / divisor,
        to: toScaled / divisor,
        intervals: step,
        labelEvery: lineLabelEvery(step),
        marks: [valueScaled / divisor],
        answer: roundedScaled / divisor,
        places: scaleK,
    };
}

/**
 * A number already on a tick of the rounding place - "round 40 to the nearest
 * ten" - cannot be drawn: the marker would sit on a labelled candidate and
 * identify the answer (ADR 0046 §3). When the sheet draws a line, move such a
 * number off the tick by a random amount short of one step, so every problem
 * draws one (ADR 0046, amendment 2026-10-02). The step is a multiple of the
 * range's lower bound, so the nudged value stays inside it.
 */
function offTheTick(valueScaled: number, step: number): number {
    return valueScaled % step === 0 ? valueScaled + getRandomInt(1, step - 1) : valueScaled;
}

/**
 * The number line for a comparing problem (ADR 0046 §2 - "the case with the
 * least prior art"): both values marked, on a line padded a tick either side
 * of the pair. The tick unit is the finest place the pair is held to, so
 * both values are exact multiples of it and always land on a tick
 * (ADR 0033 §4) - unlike rounding there is no place to round to, so the only
 * failure mode is the pair being too far apart to draw legibly, which is
 * refused the way ADR 0044 §4 refuses an over-long duration: the line is not
 * drawn, rather than coarsened.
 */
const COMPARING_LINE_MAX_TICKS = 16;

function comparingLineSpec(
    aScaled: number,
    bScaled: number,
    places: number
): RoundingNumberLineSpec | undefined {
    const lo = Math.min(aScaled, bScaled) - 1;
    const hi = Math.max(aScaled, bScaled) + 1;
    const intervals = hi - lo;
    if (intervals > COMPARING_LINE_MAX_TICKS) {
        return undefined;
    }
    const divisor = 10 ** places;
    return {
        from: lo / divisor,
        to: hi / divisor,
        intervals,
        labelEvery: lineLabelEvery(intervals),
        marks: [aScaled / divisor, bScaled / divisor],
        places,
    };
}

/** Whole-number rounding places valid for a number with this many digits. */
function wholePlacesForDigits(digits: number): number[] {
    const places: number[] = [];
    for (let exponent = 1; exponent <= digits - 1; exponent++) {
        places.push(exponent);
    }
    return places.length > 0 ? places : [1];
}

/**
 * Rounds a scaled integer to a power of ten, half **up** - written out rather
 * than deferred to Math.round, which rounds half toward +Infinity only for
 * positives and is not what a grade-3 textbook means (ADR 0006 s3).
 *
 * `scaleK` is the scale the value is held at; `exponent` is the place to round
 * to. The result stays at `scaleK`.
 */
export function roundHalfUp(scaled: number, scaleK: number, exponent: number): number {
    const step = 10 ** (exponent + scaleK);
    if (step <= 1) {
        return scaled;
    }
    const remainder = scaled % step;
    return remainder * 2 >= step ? scaled - remainder + step : scaled - remainder;
}

/**
 * Rescales two values to their shared, finer place count - the step
 * `compareScaled` needs to compare exactly, and a comparing number line
 * needs to put both values in the same tick unit.
 */
function toCommonScale(
    a: { scaled: number; places: number },
    b: { scaled: number; places: number }
): { aScaled: number; bScaled: number; places: number } {
    const places = Math.max(a.places, b.places);
    return {
        aScaled: rescale(a.scaled, a.places, places),
        bScaled: rescale(b.scaled, b.places, places),
        places,
    };
}

/** -1 when a < b, 0 when equal, 1 when a > b. Exact, across mixed scales. */
export function compareScaled(
    a: { scaled: number; places: number },
    b: { scaled: number; places: number }
): -1 | 0 | 1 {
    const { aScaled, bScaled } = toCommonScale(a, b);
    if (aScaled === bScaled) return 0;
    return aScaled < bScaled ? -1 : 1;
}

const SYMBOL: Record<-1 | 0 | 1, string> = { "-1": "<", 0: "=", 1: ">" };

// ── rounding ─────────────────────────────────────────────────────────────

/** A decimal for a rounding/comparing sheet: whole part plus `places` digits. */
function drawDecimal(places: number, maxWhole: number): { scaled: number; places: number } {
    const whole = getRandomInt(0, Math.max(0, maxWhole));
    const fraction = getRandomInt(0, 10 ** places - 1);
    return { scaled: whole * 10 ** places + fraction, places };
}

/**
 * Which exponent to round to, given the setting and whether the sheet is
 * decimals. `Mixed` picks from the places that fit.
 */
function roundingExponent(settings: ISettingsProps, decimals: number): number {
    const setting = settings.RoundingPlace;
    if (setting && setting !== RoundingPlaceOptions.Mixed) {
        return PLACE_EXPONENT[setting];
    }
    if (decimals > 0) {
        // Nearest whole always works; nearest tenth needs a hundredths digit.
        const choices = decimals >= 2 ? [0, -1] : [0];
        return choices[getRandomInt(0, choices.length - 1)];
    }
    const digits = clampDigits(settings.RoundingComparingDigits);
    const places = wholePlacesForDigits(digits);
    return places[getRandomInt(0, places.length - 1)];
}

function clampDigits(digits: number): number {
    if (!digits || digits < 2) return 2;
    if (digits > 5) return 5;
    return digits;
}

/** One rounding question, or null when the draw cannot make a fair one. */
export function buildRoundingProblem(settings: ISettingsProps, key: number): ProblemData | null {
    const language = printedLanguageOf(settings);
    const decimals = clampDecimals(settings.RoundingComparingDecimals);
    let exponent = roundingExponent(settings, decimals);

    if (decimals > 0) {
        // A hundredths digit is needed to round to the nearest tenth.
        if (exponent === -1 && decimals < 2) {
            exponent = 0;
        }
        const scaleK = decimals;
        let value = drawDecimal(scaleK, 20);
        // Roughly one in four is an exact half at the deciding place, the case
        // students get wrong and are rarely shown (ADR 0006 s3).
        if (getRandomInt(1, 4) === 1) {
            value = { ...value, scaled: forceHalf(value.scaled, scaleK, exponent) };
        }
        const drawsLine =
            settings.RoundingDisplay === RoundingDisplayOptions.NumberLine &&
            roundingLineEligible(exponent, decimals);
        if (drawsLine) {
            value = { ...value, scaled: offTheTick(value.scaled, 10 ** (exponent + scaleK)) };
        }
        const rounded = roundHalfUp(value.scaled, scaleK, exponent);
        const answerPlaces = Math.max(0, -exponent);
        const prompt = PROMPTS[language].round(formatScaled(value.scaled, scaleK), ROUND_CLAUSES[language][exponent]);
        const answer = formatScaled(rescale(rounded, scaleK, answerPlaces), answerPlaces);
        const roundingLine = drawsLine
            ? roundingLineSpec(value.scaled, rounded, scaleK, 10 ** (exponent + scaleK))
            : undefined;
        return {
            key,
            numbers: [],
            operator: "",
            roundingComparingKind: "rounding",
            display: [prompt, answer],
            roundingLine,
        };
    }

    // Whole numbers. The number must have enough digits to survive the rounding
    // - "round 47 to the nearest thousand" is zero, and a trick. Redraw the
    // number wider, never move the place (ADR 0006 s2): the heading promised it.
    const digits = Math.max(clampDigits(settings.RoundingComparingDigits), exponent + 1);
    const low = 10 ** (digits - 1);
    const high = 10 ** digits - 1;
    let value = getRandomInt(low, high);
    if (getRandomInt(1, 4) === 1) {
        // forceHalf keeps the value inside [low, high]: low is a multiple of the
        // step, so flooring to it and adding half a step cannot leave the range.
        value = forceHalf(value, 0, exponent);
    }
    const drawsLine =
        settings.RoundingDisplay === RoundingDisplayOptions.NumberLine && roundingLineEligible(exponent, 0);
    if (drawsLine) {
        value = offTheTick(value, 10 ** exponent);
    }
    const rounded = roundHalfUp(value, 0, exponent);
    const prompt = PROMPTS[language].round(withThousands(value), ROUND_CLAUSES[language][exponent]);
    const roundingLine = drawsLine ? roundingLineSpec(value, rounded, 0, 10 ** exponent) : undefined;
    return {
        key,
        numbers: [value, rounded],
        operator: "",
        roundingComparingKind: "rounding",
        display: [prompt, withThousands(rounded)],
        roundingLine,
    };
}

/** Sets the digits at and below the deciding place to an exact half (5, 0, 0…). */
function forceHalf(scaled: number, scaleK: number, exponent: number): number {
    const step = 10 ** (exponent + scaleK);
    if (step <= 1) return scaled;
    return scaled - (scaled % step) + step / 2;
}

// ── comparing ────────────────────────────────────────────────────────────

function clampDecimals(decimals: number): number {
    if (!decimals || decimals < 0) return 0;
    if (decimals > 2) return 2;
    return decimals;
}

/**
 * One comparison, or null on an accidental tie that was not meant to be one.
 *
 * The second number is built from the first so the pair shares a random number
 * of leading digits and diverges after - two independently drawn 4-digit
 * numbers differ in the thousands place nine times in ten, and a page of those
 * is answered by glancing at the first digit (ADR 0006 s4).
 */
export function buildComparingProblem(settings: ISettingsProps, key: number): ProblemData | null {
    const decimals = clampDecimals(settings.RoundingComparingDecimals);
    const equal = getRandomInt(1, 10) === 1;

    if (decimals > 0) {
        const a = drawDecimal(getRandomInt(1, decimals), 9);
        let b: { scaled: number; places: number };
        if (equal) {
            b = { ...a };
        } else {
            b = drawDecimal(getRandomInt(1, decimals), 9);
            if (getRandomInt(0, 3) !== 0) {
                // Share the whole part, so the divergence is after the point -
                // this is where 0.7 vs 0.68 comes from.
                const aWhole = Math.floor(a.scaled / 10 ** a.places);
                b = { scaled: aWhole * 10 ** b.places + (b.scaled % 10 ** b.places), places: b.places };
            }
            if (compareScaled(a, b) === 0) return null;
        }
        const relation = compareScaled(a, b);
        const common = toCommonScale(a, b);
        const roundingLine =
            settings.RoundingDisplay === RoundingDisplayOptions.NumberLine
                ? comparingLineSpec(common.aScaled, common.bScaled, common.places)
                : undefined;
        return {
            key,
            numbers: [],
            operator: "",
            roundingComparingKind: "comparing",
            display: [
                `${formatScaled(a.scaled, a.places)} ___ ${formatScaled(b.scaled, b.places)}`,
                SYMBOL[relation],
            ],
            roundingLine,
        };
    }

    const digits = clampDigits(settings.RoundingComparingDigits);
    const low = 10 ** (digits - 1);
    const a = getRandomInt(low, 10 ** digits - 1);
    let b: number;
    if (equal) {
        b = a;
    } else {
        b = divergeFrom(a, digits);
        if (b === a) return null;
    }
    const relation: -1 | 0 | 1 = a === b ? 0 : a < b ? -1 : 1;
    const roundingLine =
        settings.RoundingDisplay === RoundingDisplayOptions.NumberLine
            ? comparingLineSpec(a, b, 0)
            : undefined;
    return {
        key,
        numbers: [a, b],
        operator: "",
        roundingComparingKind: "comparing",
        display: [`${withThousands(a)} ___ ${withThousands(b)}`, SYMBOL[relation]],
        roundingLine,
    };
}

/** A number sharing a random number of leading digits with `value`. */
function divergeFrom(value: number, digits: number): number {
    const source = String(value).padStart(digits, "0").split("");
    const shared = getRandomInt(0, digits - 1);
    const original = Number(source[shared]);
    // The diverging digit differs from the original; the first digit stays 1-9.
    const min = shared === 0 ? 1 : 0;
    let replacement = getRandomInt(min, 9);
    while (replacement === original) {
        replacement = getRandomInt(min, 9);
    }
    source[shared] = String(replacement);
    for (let i = shared + 1; i < digits; i++) {
        source[i] = String(getRandomInt(0, 9));
    }
    return Number(source.join(""));
}

// ── page assembly ────────────────────────────────────────────────────────

function questionKinds(question: RoundingComparingOptions): ("rounding" | "comparing")[] {
    if (question === RoundingComparingOptions.Rounding) return ["rounding"];
    if (question === RoundingComparingOptions.Comparing) return ["comparing"];
    return ["rounding", "comparing"];
}

/** A page-shaped set of rounding/comparing questions. */
export function generateRoundingComparingPages(settings: ISettingsProps): ProblemData[][] | null {
    const kinds = questionKinds(settings.RoundingComparingQuestion ?? RoundingComparingOptions.Mixed);
    return generateProblemPages({
        settings,
        choices: kinds,
        build: (kind, key) =>
            kind === "rounding"
                ? buildRoundingProblem(settings, key)
                : buildComparingProblem(settings, key),
        fingerprint: (problem) => problem.display![0],
    });
}
